"use client";

import { useState } from "react";
import {
  ChevronLeft,
  Droplets,
  Droplet,
  Lightbulb,
  BookOpen,
  Flower2,
  Film,
  Quote,
  MessageCircleHeart,
  Clock,
  Gift,
  Wrench,
  Hand,
  Heart,
  Sparkles,
  Palette,
  Smile,
  Copy,
  Check,
  HeartHandshake,
  AlertTriangle,
  Users,
  Cross,
  Book,
  Play,
} from "lucide-react";
import { useTranslations, useLocale } from "next-intl";
import { useRouter } from "next/navigation";
import { LoveLanguage } from "@/types";
import { VideoDialog, VideoThumbnail } from "@/components/ui/video-dialog";

interface GuideDetailClientProps {
  langId: LoveLanguage;
}

interface ChristianVideo {
  id: string;
  title: string;
  channel: string;
}

interface ChristianMovie {
  title: string;
  year: string;
  description: string;
}

interface ChristianBook {
  title: string;
  author: string;
  description: string;
}

const LANG_CONFIG = {
  [LoveLanguage.A]: {
    icon: MessageCircleHeart,
    gradient: "from-amber-500 to-orange-500",
    bg: "bg-amber-50 dark:bg-amber-900/20",
    border: "border-amber-200 dark:border-amber-700",
    text: "text-amber-800 dark:text-amber-200",
    tagBg: "bg-amber-100 dark:bg-amber-800/40",
    swatchPos: ["#FEF3C7", "#FCD34D", "#F59E0B"],
    swatchNeg: ["#374151", "#1F2937", "#991B1B"],
  },
  [LoveLanguage.B]: {
    icon: Clock,
    gradient: "from-teal-500 to-cyan-500",
    bg: "bg-teal-50 dark:bg-teal-900/20",
    border: "border-teal-200 dark:border-teal-700",
    text: "text-teal-800 dark:text-teal-200",
    tagBg: "bg-teal-100 dark:bg-teal-800/40",
    swatchPos: ["#CCFBF1", "#5EEAD4", "#14B8A6"],
    swatchNeg: ["#EF4444", "#F97316", "#1F2937"],
  },
  [LoveLanguage.C]: {
    icon: Gift,
    gradient: "from-rose-500 to-pink-500",
    bg: "bg-rose-50 dark:bg-rose-900/20",
    border: "border-rose-200 dark:border-rose-700",
    text: "text-rose-800 dark:text-rose-200",
    tagBg: "bg-rose-100 dark:bg-rose-800/40",
    swatchPos: ["#FCE7F3", "#F472B6", "#EC4899"],
    swatchNeg: ["#9CA3AF", "#6B7280", "#78350F"],
  },
  [LoveLanguage.D]: {
    icon: Wrench,
    gradient: "from-blue-500 to-indigo-500",
    bg: "bg-blue-50 dark:bg-blue-900/20",
    border: "border-blue-200 dark:border-blue-700",
    text: "text-blue-800 dark:text-blue-200",
    tagBg: "bg-blue-100 dark:bg-blue-800/40",
    swatchPos: ["#DBEAFE", "#60A5FA", "#2563EB"],
    swatchNeg: ["#FCD34D", "#F97316", "#92400E"],
  },
  [LoveLanguage.E]: {
    icon: Hand,
    gradient: "from-orange-500 to-red-500",
    bg: "bg-orange-50 dark:bg-orange-900/20",
    border: "border-orange-200 dark:border-orange-700",
    text: "text-orange-800 dark:text-orange-200",
    tagBg: "bg-orange-100 dark:bg-orange-800/40",
    swatchPos: ["#FED7AA", "#FB923C", "#EA580C"],
    swatchNeg: ["#BFDBFE", "#E5E7EB", "#9CA3AF"],
  },
};

const EMOJI_NAMES: Record<string, string> = {
  "❤️": "Red Heart",
  "🥹": "Teary Eyes",
  "😍": "Heart Eyes",
  "🫶": "Heart Hands",
  "💌": "Love Letter",
  "🥰": "Smiling Hearts",
  "💕": "Two Hearts",
  "✨": "Sparkles",
  "💝": "Heart Ribbon",
  "🦋": "Butterfly",
  "🤍": "White Heart",
  "🫂": "Hug",
  "🥺": "Pleading",
  "🌟": "Star",
  "🕊️": "Dove",
  "💛": "Yellow Heart",
  "🦢": "Swan",
  "🎁": "Gift",
  "🌹": "Rose",
  "🎀": "Ribbon",
  "🧸": "Teddy",
  "💐": "Bouquet",
  "🎉": "Party",
  "🛍️": "Shopping",
  "🛒": "Cart",
  "💪": "Strong",
  "🤲": "Palms Up",
  "🫡": "Salute",
  "🙏": "Pray",
  "🛠️": "Tools",
  "🥘": "Food",
  "🚗": "Car",
  "🧹": "Broom",
  "🤗": "Hugging",
  "💏": "Kiss",
  "❤️‍🔥": "Fire Heart",
  "💆‍♀️": "Massage",
  "🤌": "Pinch",
  "✋": "Hand",
};

export default function GuideDetailClient({ langId }: GuideDetailClientProps) {
  const t = useTranslations();
  const locale = useLocale();
  const router = useRouter();
  const isRTL = locale === "ar";
  const config = LANG_CONFIG[langId];
  const Icon = config.icon;
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<ChristianVideo | null>(null);
  const [activeTab, setActiveTab] = useState<"movies" | "books" | "videos">("movies");

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const posItems = t.raw(`info_pos_${langId}`) as string[];
  const negItems = t.raw(`info_neg_${langId}`) as string[];
  const symbols = (t.raw(`info_symbols_${langId}`) || []) as string[];
  const culture = (t.raw(`info_culture_${langId}`) || []) as string[];
  const emojisStr = (t(`info_emojis_${langId}`) || "") as string;
  const emojis = Array.from(emojisStr).filter((e) => e.trim());

  const expressItems = (t.raw(`info_express_${langId}`) || []) as string[];
  const mistakesItems = (t.raw(`info_mistakes_${langId}`) || []) as string[];
  const partnerItems = (t.raw(`info_partner_${langId}`) || []) as string[];

  const christianMovies = (t.raw(`info_christian_movies_${langId}`) || []) as ChristianMovie[];
  const christianBooks = (t.raw(`info_christian_books_${langId}`) || []) as ChristianBook[];
  const christianVideos = (t.raw(`info_christian_videos_${langId}`) || []) as ChristianVideo[];

  const descriptionParagraphs = t(`info_full_desc_${langId}`).split("\n\n");

  return (
    <div
      className="w-full max-w-3xl mx-auto px-4 py-6 pb-24 relative"
      data-testid="info-detail"
    >
      <div className="absolute inset-0 bg-mesh-gradient pointer-events-none" />

      <div className="relative z-10">
        <div
          className="flex items-center gap-4 mb-8"
          data-testid="info-detail-header"
        >
          <button
            onClick={() => router.back()}
            className="p-2.5 rounded-xl hover:bg-secondary transition-all cursor-pointer"
            data-testid="info-detail-back"
            aria-label={t("btn_back")}
          >
            <ChevronLeft className={`w-6 h-6 ${isRTL ? "rotate-180" : ""}`} />
          </button>
          <div
            className={`flex items-center gap-3 px-4 py-2 rounded-2xl ${config.bg} ${config.border} border`}
          >
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br ${config.gradient}`}
            >
              <Icon className="w-5 h-5 text-white" />
            </div>
            <h1
              className="font-display text-xl md:text-2xl font-semibold text-foreground"
              data-testid="info-detail-title"
            >
              {t(`lang_${langId}`)}
            </h1>
          </div>
        </div>

        <div className="grid gap-6">
          {/* Understanding This Language - Expanded */}
          <div
            className={`rounded-3xl p-6 md:p-8 ${config.bg} border ${config.border} card-elevated opacity-0 animate-reveal-up`}
            data-testid="info-detail-hero"
          >
            <div className="flex items-start gap-4">
              <div
                className={`p-3 rounded-2xl bg-gradient-to-br ${config.gradient} shadow-lg flex-shrink-0`}
              >
                <Lightbulb className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="font-display text-lg font-semibold text-foreground mb-3">
                  {t("info_desc_title")}
                </h2>
                <div className="space-y-4">
                  {descriptionParagraphs.map((paragraph, i) => (
                    <p key={i} className="text-foreground/80 leading-relaxed text-base">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Fills and Drains Bucket */}
          <div className="grid md:grid-cols-2 gap-5">
            <div
              className="bg-card border border-border rounded-3xl p-6 card-elevated opacity-0 animate-reveal-up stagger-1"
              data-testid="info-detail-fills"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2.5 rounded-xl bg-emerald-100 dark:bg-emerald-900/30">
                  <Droplets className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h3 className="font-display font-semibold text-emerald-700 dark:text-emerald-300">
                  {t("info_fills_bucket")}
                </h3>
              </div>
              <ul className="space-y-3">
                {posItems.map((item, i) => (
                  <li key={i} className="flex gap-3 text-foreground/90">
                    <Heart className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0 fill-emerald-500/20" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div
              className="bg-card border border-border rounded-3xl p-6 card-elevated opacity-0 animate-reveal-up stagger-2"
              data-testid="info-detail-drains"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2.5 rounded-xl bg-red-100 dark:bg-red-900/30">
                  <Droplet className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="font-display font-semibold text-red-700 dark:text-red-300">
                  {t("info_drains_bucket")}
                </h3>
              </div>
              <ul className="space-y-3">
                {negItems.map((item, i) => (
                  <li key={i} className="flex gap-3 text-foreground/90">
                    <span className="w-4 h-4 mt-1 flex-shrink-0 text-red-400 font-bold">
                      -
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* NEW: How to Express This Language */}
          {expressItems.length > 0 && (
            <div
              className="bg-card border border-border rounded-3xl p-6 card-elevated opacity-0 animate-reveal-up"
              style={{ animationDelay: "0.25s" }}
              data-testid="info-detail-express"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2.5 rounded-xl bg-primary/10">
                  <HeartHandshake className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-display font-semibold text-foreground">
                  {t("info_express_title")}
                </h3>
              </div>
              <ul className="space-y-3">
                {expressItems.map((item, i) => (
                  <li key={i} className="flex gap-3 text-foreground/90">
                    <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* NEW: Common Mistakes */}
          {mistakesItems.length > 0 && (
            <div
              className="bg-card border border-border rounded-3xl p-6 card-elevated opacity-0 animate-reveal-up"
              style={{ animationDelay: "0.3s" }}
              data-testid="info-detail-mistakes"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2.5 rounded-xl bg-amber-100 dark:bg-amber-900/30">
                  <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <h3 className="font-display font-semibold text-amber-700 dark:text-amber-300">
                  {t("info_mistakes_title")}
                </h3>
              </div>
              <ul className="space-y-3">
                {mistakesItems.map((item, i) => (
                  <li key={i} className="flex gap-3 text-foreground/90">
                    <span className="w-4 h-4 mt-1 flex-shrink-0 text-amber-500">✗</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* NEW: Tips for Partners */}
          {partnerItems.length > 0 && (
            <div
              className="bg-card border border-border rounded-3xl p-6 card-elevated opacity-0 animate-reveal-up"
              style={{ animationDelay: "0.35s" }}
              data-testid="info-detail-partner"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2.5 rounded-xl bg-pink-100 dark:bg-pink-900/30">
                  <Users className="w-5 h-5 text-pink-600 dark:text-pink-400" />
                </div>
                <h3 className="font-display font-semibold text-pink-700 dark:text-pink-300">
                  {t("info_partner_title")}
                </h3>
              </div>
              <ul className="space-y-3">
                {partnerItems.map((item, i) => (
                  <li key={i} className="flex gap-3 text-foreground/90">
                    <span className="text-pink-500 mt-0.5">💡</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* NEW: In Different Relationships */}
          <div
            className={`rounded-3xl p-6 ${config.bg} border ${config.border} opacity-0 animate-reveal-up`}
            style={{ animationDelay: "0.4s" }}
            data-testid="info-detail-relationships"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className={`p-2.5 rounded-xl bg-gradient-to-br ${config.gradient}`}>
                <Users className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-display font-semibold text-foreground">
                {t("info_relationships_title")}
              </h3>
            </div>
            <div className="grid gap-4">
              <div className="bg-card/50 rounded-xl p-4 border border-border/50">
                <h4 className="font-semibold text-foreground text-sm mb-2 flex items-center gap-2">
                  <span>💑</span> {t("info_relationships_romantic_title")}
                </h4>
                <p className="text-foreground/70 text-sm leading-relaxed">
                  {t(`info_relationships_romantic_${langId}`)}
                </p>
              </div>
              <div className="bg-card/50 rounded-xl p-4 border border-border/50">
                <h4 className="font-semibold text-foreground text-sm mb-2 flex items-center gap-2">
                  <span>👥</span> {t("info_relationships_friends_title")}
                </h4>
                <p className="text-foreground/70 text-sm leading-relaxed">
                  {t(`info_relationships_friends_${langId}`)}
                </p>
              </div>
              <div className="bg-card/50 rounded-xl p-4 border border-border/50">
                <h4 className="font-semibold text-foreground text-sm mb-2 flex items-center gap-2">
                  <span>👨‍👩‍👧</span> {t("info_relationships_family_title")}
                </h4>
                <p className="text-foreground/70 text-sm leading-relaxed">
                  {t(`info_relationships_family_${langId}`)}
                </p>
              </div>
              <div className="bg-card/50 rounded-xl p-4 border border-border/50">
                <h4 className="font-semibold text-foreground text-sm mb-2 flex items-center gap-2">
                  <span>💼</span> {t("info_relationships_work_title")}
                </h4>
                <p className="text-foreground/70 text-sm leading-relaxed">
                  {t(`info_relationships_work_${langId}`)}
                </p>
              </div>
            </div>
          </div>

          {/* Signals */}
          <div
            className={`rounded-3xl p-6 ${config.bg} border ${config.border} opacity-0 animate-reveal-up`}
            style={{ animationDelay: "0.45s" }}
            data-testid="info-detail-signals"
          >
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="w-5 h-5 text-foreground/70" />
              <h3 className="font-display font-semibold text-foreground">
                {t("info_signals_title")}
              </h3>
            </div>
            <p className="text-foreground/70 italic leading-relaxed">
              &ldquo;{t(`info_signals_${langId}`)}&rdquo;
            </p>
          </div>

          {/* Bible Verse */}
          <div
            className="bg-card border border-border rounded-3xl p-6 card-elevated opacity-0 animate-reveal-up"
            style={{ animationDelay: "0.5s" }}
            data-testid="info-detail-quote"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-xl bg-primary/10">
                <Quote className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-display font-semibold text-primary">
                {t("info_bible_verse")}
              </h3>
            </div>
            <blockquote className="text-foreground/90 italic leading-relaxed border-l-4 border-primary/30 pl-4 rtl:border-l-0 rtl:border-r-4 rtl:pl-0 rtl:pr-4">
              {t(`quote_${langId}`)}
            </blockquote>
          </div>

          {/* NEW: Christian Resources Section */}
          {(christianMovies.length > 0 || christianBooks.length > 0 || christianVideos.length > 0) && (
            <div
              className="bg-card border border-border rounded-3xl p-6 card-elevated opacity-0 animate-reveal-up"
              style={{ animationDelay: "0.55s" }}
              data-testid="info-detail-christian"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2.5 rounded-xl bg-indigo-100 dark:bg-indigo-900/30">
                  <Cross className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <h3 className="font-display font-semibold text-foreground">
                  {t("info_christian_title")}
                </h3>
              </div>

              {/* Tabs */}
              <div className="flex gap-2 mb-5 border-b border-border pb-3">
                <button
                  onClick={() => setActiveTab("movies")}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === "movies"
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary hover:bg-secondary/80 text-foreground"
                  }`}
                >
                  <Film className="w-4 h-4 inline-block mr-2" />
                  {t("info_christian_movies_tab")}
                </button>
                <button
                  onClick={() => setActiveTab("books")}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === "books"
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary hover:bg-secondary/80 text-foreground"
                  }`}
                >
                  <Book className="w-4 h-4 inline-block mr-2" />
                  {t("info_christian_books_tab")}
                </button>
                <button
                  onClick={() => setActiveTab("videos")}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === "videos"
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary hover:bg-secondary/80 text-foreground"
                  }`}
                >
                  <Play className="w-4 h-4 inline-block mr-2" />
                  {t("info_christian_videos_tab")}
                </button>
              </div>

              {/* Movies Tab */}
              {activeTab === "movies" && christianMovies.length > 0 && (
                <div>
                  <p className="text-muted-foreground text-sm mb-4">{t("info_christian_movies_desc")}</p>
                  <div className="space-y-3">
                    {christianMovies.map((movie, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-secondary/50">
                        <span className="text-lg flex-shrink-0">🎬</span>
                        <div>
                          <h4 className="font-semibold text-foreground text-sm">
                            {movie.title} <span className="text-muted-foreground font-normal">({movie.year})</span>
                          </h4>
                          <p className="text-foreground/70 text-sm mt-1">{movie.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Books Tab */}
              {activeTab === "books" && christianBooks.length > 0 && (
                <div>
                  <p className="text-muted-foreground text-sm mb-4">{t("info_christian_books_desc")}</p>
                  <div className="space-y-3">
                    {christianBooks.map((book, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-secondary/50">
                        <span className="text-lg flex-shrink-0">📖</span>
                        <div>
                          <h4 className="font-semibold text-foreground text-sm">{book.title}</h4>
                          <p className="text-muted-foreground text-xs">by {book.author}</p>
                          <p className="text-foreground/70 text-sm mt-1">{book.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Videos Tab */}
              {activeTab === "videos" && christianVideos.length > 0 && (
                <div>
                  <p className="text-muted-foreground text-sm mb-4">{t("info_christian_videos_desc")}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {christianVideos.map((video, i) => (
                      <VideoThumbnail
                        key={i}
                        videoId={video.id}
                        title={video.title}
                        channel={video.channel}
                        onClick={() => setSelectedVideo(video)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Symbols */}
          {symbols.length > 0 && (
            <div
              className="bg-card border border-border rounded-3xl p-6 card-elevated opacity-0 animate-reveal-up"
              style={{ animationDelay: "0.6s" }}
              data-testid="info-detail-symbols"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2.5 rounded-xl bg-secondary">
                  <Flower2 className="w-5 h-5 text-foreground/70" />
                </div>
                <h3 className="font-display font-semibold text-foreground">
                  {t("info_symbols_title")}
                </h3>
              </div>
              <p className="text-foreground/60 text-sm mb-4">
                {t(`info_symbols_desc_${langId}`)}
              </p>
              <div className="flex flex-wrap gap-2">
                {symbols.map((symbol, i) => (
                  <span
                    key={i}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium ${config.tagBg} text-foreground ${config.border} border`}
                  >
                    {symbol}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Visual Identity */}
          <div
            className="bg-card border border-border rounded-3xl p-6 card-elevated opacity-0 animate-reveal-up"
            style={{ animationDelay: "0.65s" }}
            data-testid="info-detail-visual"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-xl bg-secondary">
                <Palette className="w-5 h-5 text-foreground/70" />
              </div>
              <h3 className="font-display font-semibold text-foreground">
                {t("info_visual_title")}
              </h3>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <p className="text-xs uppercase font-bold text-foreground/50 mb-3 tracking-wider">
                  {t("info_colors_pos_label")}
                </p>
                <div className="flex flex-wrap gap-3">
                  {config.swatchPos.map((color, i) => {
                    const id = `color-pos-${langId}-${i}`;
                    const isCopied = copiedId === id;
                    return (
                      <button
                        key={i}
                        onClick={() => handleCopy(color, id)}
                        className="group flex flex-col items-center gap-1.5 cursor-pointer"
                        title={`${color} - ${t("click_to_copy")}`}
                      >
                        <div
                          className="w-12 h-12 rounded-xl shadow-md border border-black/10 dark:border-white/10 transition-transform group-hover:scale-110 flex items-center justify-center"
                          style={{ backgroundColor: color }}
                        >
                          {isCopied ? (
                            <Check className="w-5 h-5 text-emerald-600 drop-shadow" />
                          ) : (
                            <Copy className="w-4 h-4 opacity-0 group-hover:opacity-70 text-gray-700 drop-shadow transition-opacity" />
                          )}
                        </div>
                        <span className="text-[10px] font-mono text-foreground/50 group-hover:text-foreground transition-colors">
                          {color}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <p className="text-xs uppercase font-bold text-foreground/50 mb-3 mt-6 tracking-wider">
                  {t("info_colors_neg_label")}
                </p>
                <div className="flex flex-wrap gap-3">
                  {config.swatchNeg.map((color, i) => {
                    const id = `color-neg-${langId}-${i}`;
                    const isCopied = copiedId === id;
                    return (
                      <button
                        key={i}
                        onClick={() => handleCopy(color, id)}
                        className="group flex flex-col items-center gap-1.5 cursor-pointer"
                        title={`${color} - ${t("click_to_copy")}`}
                      >
                        <div
                          className="w-12 h-12 rounded-xl shadow-md border border-black/10 dark:border-white/10 transition-transform group-hover:scale-110 flex items-center justify-center"
                          style={{ backgroundColor: color }}
                        >
                          {isCopied ? (
                            <Check className="w-5 h-5 text-white drop-shadow" />
                          ) : (
                            <Copy className="w-4 h-4 opacity-0 group-hover:opacity-70 text-white drop-shadow transition-opacity" />
                          )}
                        </div>
                        <span className="text-[10px] font-mono text-foreground/50 group-hover:text-foreground transition-colors">
                          {color}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Smile className="w-4 h-4 text-foreground/50" />
                  <p className="text-xs uppercase font-bold text-foreground/50 tracking-wider">
                    {t("info_emojis_label")}
                  </p>
                </div>
                <p className="text-foreground/50 text-xs mb-4">
                  {t("info_emojis_hint")}
                </p>
                <div className="flex flex-wrap gap-2 bg-secondary/50 p-4 rounded-xl border border-border">
                  {emojis.map((emoji, i) => {
                    const id = `emoji-${langId}-${i}`;
                    const isCopied = copiedId === id;
                    const name = EMOJI_NAMES[emoji] || "Emoji";
                    return (
                      <button
                        key={i}
                        onClick={() => handleCopy(emoji, id)}
                        className="relative w-12 h-12 flex items-center justify-center text-3xl hover:bg-card hover:scale-125 transition-all rounded-xl cursor-pointer group border border-transparent hover:border-border hover:shadow-md"
                        title={`${name} - ${t("click_to_copy")}`}
                      >
                        {isCopied ? (
                          <Check className="w-6 h-6 text-emerald-500" />
                        ) : (
                          emoji
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Popular Culture */}
          {culture.length > 0 && (
            <div
              className="bg-card border border-border rounded-3xl p-6 card-elevated opacity-0 animate-reveal-up"
              style={{ animationDelay: "0.7s" }}
              data-testid="info-detail-culture"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2.5 rounded-xl bg-secondary">
                  <Film className="w-5 h-5 text-muted-foreground" />
                </div>
                <h3 className="font-display font-semibold text-foreground">
                  {t("info_culture_title")}
                </h3>
              </div>
              <p className="text-muted-foreground text-sm mb-4">
                {t("info_culture_desc")}
              </p>
              <div className="space-y-3">
                {culture.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 p-3 rounded-xl bg-secondary/50"
                  >
                    <span className="text-lg flex-shrink-0">🎬</span>
                    <span className="text-foreground/90 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Science */}
          <div
            className="bg-secondary/50 border border-border rounded-2xl p-5 opacity-0 animate-reveal-up"
            style={{ animationDelay: "0.75s" }}
            data-testid="info-detail-science"
          >
            <div className="flex items-start gap-3">
              <BookOpen className="w-5 h-5 text-foreground/50 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-foreground text-sm mb-2">
                  {t("info_science_title")}
                </h4>
                <p className="text-foreground/60 text-sm leading-relaxed">
                  {t("info_science_content")}
                </p>
              </div>
            </div>
          </div>

          <p
            className="text-center text-xs text-foreground/40 mt-6 opacity-0 animate-reveal-up"
            style={{ animationDelay: "0.8s" }}
          >
            {t("info_suggestions_disclaimer")}
          </p>
        </div>

        <div className="mt-8 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-5">
          <div className="flex gap-3">
            <span className="text-xl">💡</span>
            <p className="text-sm text-amber-950 dark:text-amber-100 leading-relaxed">
              {t("did_you_know")}
            </p>
          </div>
        </div>
      </div>

      {/* Video Dialog */}
      {selectedVideo && (
        <VideoDialog
          videoId={selectedVideo.id}
          title={selectedVideo.title}
          channel={selectedVideo.channel}
          isOpen={!!selectedVideo}
          onClose={() => setSelectedVideo(null)}
        />
      )}
    </div>
  );
}
